import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    makeStyles,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import CenterFocusStrongIcon from '@material-ui/icons/CenterFocusStrong';
import { useTranslate } from 'react-admin';
import { CameraDevice, Html5Qrcode } from 'html5-qrcode';
import { CRM_COLORS } from '../../theme/crmColors';
import { BREAKPOINTS } from '../../theme/breakpoints';

const SCANNER_REGION_ID = 'anc-qr-scanner-region';

const useStyles = makeStyles(() => ({
    dialogPaper: {
        margin: 0,
        maxWidth: '100%',
        width: '100%',
        height: '100%',
        maxHeight: '100%',
        borderRadius: 0,
        backgroundColor: '#0a1628',
        color: CRM_COLORS.surface,
        overflow: 'hidden',
    },
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: '100%',
        background: `linear-gradient(180deg, ${CRM_COLORS.secondary} 0%, #0a1628 28%, #0a1628 100%)`,
    },
    topBar: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        flexShrink: 0,
        zIndex: 2,
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            padding: '10px 12px',
            paddingTop: 'max(10px, env(safe-area-inset-top))',
        },
    },
    titleBlock: {
        flex: 1,
        minWidth: 0,
    },
    title: {
        fontSize: '1.05rem',
        fontWeight: 600,
        color: CRM_COLORS.surface,
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
    },
    subtitle: {
        fontSize: '0.75rem',
        color: alpha(CRM_COLORS.surface, 0.72),
        marginTop: 2,
        lineHeight: 1.35,
    },
    closeButton: {
        color: CRM_COLORS.surface,
        backgroundColor: alpha(CRM_COLORS.surface, 0.1),
        borderRadius: 10,
        padding: 8,
        '&:hover': {
            backgroundColor: alpha(CRM_COLORS.surface, 0.18),
        },
    },
    stage: {
        position: 'relative',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            padding: 12,
        },
    },
    viewport: {
        width: '100%',
        maxWidth: 520,
        height: '100%',
        maxHeight: 'min(72vh, 560px)',
        backgroundColor: '#000',
        borderRadius: 16,
        overflow: 'hidden',
        position: 'relative',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.45)',
        border: `1px solid ${alpha(CRM_COLORS.primary, 0.35)}`,
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            maxHeight: 'none',
            height: '100%',
            borderRadius: 12,
        },
    },
    frameOverlay: {
        pointerEvents: 'none',
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
    },
    frame: {
        width: 'min(68%, 260px)',
        aspectRatio: '1 / 1',
        position: 'relative',
    },
    corner: {
        position: 'absolute',
        width: 28,
        height: 28,
        borderColor: CRM_COLORS.primaryLight,
        borderStyle: 'solid',
    },
    cornerTL: {
        top: 0,
        left: 0,
        borderWidth: '3px 0 0 3px',
        borderTopLeftRadius: 6,
    },
    cornerTR: {
        top: 0,
        right: 0,
        borderWidth: '3px 3px 0 0',
        borderTopRightRadius: 6,
    },
    cornerBL: {
        bottom: 0,
        left: 0,
        borderWidth: '0 0 3px 3px',
        borderBottomLeftRadius: 6,
    },
    cornerBR: {
        bottom: 0,
        right: 0,
        borderWidth: '0 3px 3px 0',
        borderBottomRightRadius: 6,
    },
    scanHint: {
        position: 'absolute',
        left: 16,
        right: 16,
        bottom: 16,
        zIndex: 2,
        textAlign: 'center',
        fontSize: '0.8125rem',
        color: alpha(CRM_COLORS.surface, 0.9),
        backgroundColor: alpha('#0a1628', 0.55),
        backdropFilter: 'blur(6px)',
        borderRadius: 8,
        padding: '8px 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
    },
    bottomBar: {
        flexShrink: 0,
        padding: '16px 20px',
        paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
        background: `linear-gradient(180deg, transparent 0%, ${alpha(CRM_COLORS.secondary, 0.92)} 28%, ${CRM_COLORS.secondary} 100%)`,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        zIndex: 2,
    },
    cameraSelect: {
        '& .MuiInputLabel-root': {
            color: alpha(CRM_COLORS.surface, 0.75),
        },
        '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
            color: CRM_COLORS.primaryLight,
            backgroundColor: CRM_COLORS.secondary,
            padding: '0 4px',
        },
        '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            color: CRM_COLORS.surface,
            backgroundColor: alpha(CRM_COLORS.surface, 0.08),
            '& fieldset': {
                borderColor: alpha(CRM_COLORS.primaryLight, 0.45),
            },
            '&:hover fieldset': {
                borderColor: CRM_COLORS.primaryLight,
            },
            '&.Mui-focused fieldset': {
                borderColor: CRM_COLORS.primary,
            },
        },
        '& .MuiSelect-icon': {
            color: alpha(CRM_COLORS.surface, 0.7),
        },
    },
    menuPaper: {
        backgroundColor: CRM_COLORS.secondary,
        color: CRM_COLORS.surface,
    },
    errorBox: {
        borderRadius: 8,
        padding: '10px 12px',
        backgroundColor: alpha(CRM_COLORS.error, 0.18),
        border: `1px solid ${alpha(CRM_COLORS.error, 0.45)}`,
        color: '#ffcdd2',
        fontSize: '0.8125rem',
        lineHeight: 1.4,
    },
    cancelButton: {
        alignSelf: 'stretch',
        height: 44,
        borderRadius: 10,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.9375rem',
        color: CRM_COLORS.surface,
        borderColor: alpha(CRM_COLORS.surface, 0.35),
        '&:hover': {
            borderColor: CRM_COLORS.surface,
            backgroundColor: alpha(CRM_COLORS.surface, 0.08),
        },
    },
}));

function safeStopScanner(scanner: Html5Qrcode | null) {
    if (!scanner) return;
    try {
        const promise = scanner.stop();
        if (promise && typeof promise.catch === 'function') {
            promise.catch(() => {});
        }
    } catch {
        // El escáner puede no haber arrancado todavía — ignorar.
    }
}

function pickDefaultCameraId(cameras: CameraDevice[]): string {
    const back = cameras.find((cam) => /back|rear|environment/i.test(cam.label));
    return back?.id ?? cameras[0].id;
}

interface QrScannerDialogProps {
    open: boolean;
    onClose: () => void;
    onDecoded: (text: string) => void;
}

const QrScannerDialog: React.FC<QrScannerDialogProps> = ({ open, onClose, onDecoded }) => {
    const classes = useStyles();
    const translate = useTranslate();
    const viewportRef = useRef<HTMLDivElement>(null);
    const scannerRef = useRef<Html5Qrcode | null>(null);
    const [cameras, setCameras] = useState<CameraDevice[]>([]);
    const [selectedCameraId, setSelectedCameraId] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    // Guardamos la última callback en un ref para NO re-disparar el efecto del
    // escáner cuando cambia su identidad (el padre la recrea en cada render de
    // la lista). Si estuviera en las deps, la cámara se reiniciaría en bucle.
    const onDecodedRef = useRef(onDecoded);
    useEffect(() => {
        onDecodedRef.current = onDecoded;
    }, [onDecoded]);

    // Enumerar cámaras al abrir; resetear estado al cerrar.
    useEffect(() => {
        if (!open) {
            setCameras([]);
            setSelectedCameraId('');
            setError(null);
            return;
        }

        let cancelled = false;

        const listCameras = async () => {
            try {
                setError(null);
                const devices = await Html5Qrcode.getCameras();
                if (cancelled) return;
                if (devices.length === 0) {
                    setError(translate('app.ancs.qrScan.noCamera'));
                    return;
                }
                setCameras(devices);
                setSelectedCameraId((prev) =>
                    prev && devices.some((d) => d.id === prev) ? prev : pickDefaultCameraId(devices),
                );
            } catch {
                if (!cancelled) {
                    setError(translate('app.ancs.qrScan.noCamera'));
                }
            }
        };

        listCameras();

        return () => {
            cancelled = true;
        };
    }, [open, translate]);

    // Arrancar / reiniciar el escáner cuando hay una cámara seleccionada.
    useEffect(() => {
        if (!open || !selectedCameraId || !viewportRef.current) {
            safeStopScanner(scannerRef.current);
            scannerRef.current = null;
            return;
        }

        let cancelled = false;
        let decoded = false;
        let html5QrCode: Html5Qrcode | null = null;
        let scannerDiv: HTMLDivElement | null = null;

        const start = async () => {
            try {
                setError(null);
                scannerDiv = document.createElement('div');
                scannerDiv.id = SCANNER_REGION_ID;
                scannerDiv.style.width = '100%';
                scannerDiv.style.height = '100%';
                viewportRef.current!.appendChild(scannerDiv);

                html5QrCode = new Html5Qrcode(SCANNER_REGION_ID);
                scannerRef.current = html5QrCode;

                const boxSize = Math.min(250, Math.floor(Math.min(window.innerWidth, window.innerHeight) * 0.45));

                await html5QrCode.start(
                    selectedCameraId,
                    { fps: 10, qrbox: { width: boxSize, height: boxSize } },
                    (decodedText) => {
                        if (cancelled || decoded) return;
                        // Paramos el escáner en el primer decode para evitar
                        // disparos múltiples mientras se cierra el diálogo.
                        decoded = true;
                        safeStopScanner(scannerRef.current);
                        onDecodedRef.current(decodedText);
                    },
                    () => {},
                );
            } catch (err) {
                if (cancelled) return;
                let msg: string;
                if (err instanceof Error) {
                    const lower = err.message.toLowerCase();
                    if (
                        lower.includes('notreadable') ||
                        lower.includes('could not start') ||
                        lower.includes('occupied') ||
                        lower.includes('in use')
                    ) {
                        msg = translate('app.ancs.qrScan.cameraNotAvailable');
                    } else if (lower.includes('permission') || lower.includes('denied')) {
                        msg = translate('app.ancs.qrScan.permissionDenied');
                    } else if (lower.includes('not found') || lower.includes('no cameras')) {
                        msg = translate('app.ancs.qrScan.noCamera');
                    } else {
                        msg = err.message;
                    }
                } else {
                    msg = translate('app.ancs.qrScan.noCamera');
                }
                setError(msg);
            }
        };

        start();

        return () => {
            cancelled = true;
            safeStopScanner(html5QrCode);
            scannerRef.current = null;
            if (scannerDiv && scannerDiv.parentNode) {
                scannerDiv.parentNode.removeChild(scannerDiv);
            }
        };
        // El efecto solo depende de abrir/cerrar y de la cámara elegida.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, selectedCameraId]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen
            keepMounted
            PaperProps={{ className: classes.dialogPaper }}
        >
            <Box className={classes.root}>
                <Box className={classes.topBar}>
                    <Box className={classes.titleBlock}>
                        <Typography className={classes.title}>{translate('app.ancs.qrScan.title')}</Typography>
                        <Typography className={classes.subtitle}>{translate('app.ancs.qrScan.hint')}</Typography>
                    </Box>
                    <IconButton className={classes.closeButton} onClick={onClose} aria-label={translate('ra.action.cancel')}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box className={classes.stage}>
                    <div ref={viewportRef} className={classes.viewport}>
                        <Box className={classes.frameOverlay} aria-hidden>
                            <Box className={classes.frame}>
                                <span className={`${classes.corner} ${classes.cornerTL}`} />
                                <span className={`${classes.corner} ${classes.cornerTR}`} />
                                <span className={`${classes.corner} ${classes.cornerBL}`} />
                                <span className={`${classes.corner} ${classes.cornerBR}`} />
                            </Box>
                        </Box>
                        {!error && (
                            <Box className={classes.scanHint}>
                                <CenterFocusStrongIcon fontSize='small' />
                                <span>{translate('app.ancs.qrScan.hint')}</span>
                            </Box>
                        )}
                    </div>
                </Box>

                <Box className={classes.bottomBar}>
                    {cameras.length > 0 && (
                        <FormControl variant='outlined' fullWidth size='small' className={classes.cameraSelect}>
                            <InputLabel id='qr-scanner-camera-label'>
                                {translate('app.ancs.qrScan.selectCamera')}
                            </InputLabel>
                            <Select
                                labelId='qr-scanner-camera-label'
                                label={translate('app.ancs.qrScan.selectCamera')}
                                value={selectedCameraId}
                                onChange={(e) => setSelectedCameraId(e.target.value as string)}
                                MenuProps={{
                                    PaperProps: { className: classes.menuPaper },
                                }}
                            >
                                {cameras.map((cam, index) => (
                                    <MenuItem key={cam.id} value={cam.id}>
                                        {cam.label || `${translate('app.ancs.qrScan.camera')} ${index + 1}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {error && (
                        <Typography className={classes.errorBox} role='alert'>
                            {error}
                        </Typography>
                    )}

                    <Button variant='outlined' className={classes.cancelButton} onClick={onClose}>
                        {translate('ra.action.cancel')}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default QrScannerDialog;
